import React from 'react';

import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  from,
} from '@apollo/client';
import { onError, ErrorHandler } from '@apollo/client/link/error';
import { createHttpLink } from '@apollo/client/link/http';
import { RetryLink } from '@apollo/client/link/retry';
import { SentryLink } from 'apollo-link-sentry';
import isEqual from 'lodash/isEqual';

import Sentry from 'config/sentry';

import introspectionQueryResultData from 'graphql/generated/graphql.fragmentTypes.json';

const isDevelopment = () => process.env.IS_PRODUCTION !== 'true';

const httpLink = createHttpLink({
  uri: process.env.REACT_APP_GRAPHQL_URL,
  credentials: 'include',
});

export const errorHandler: ErrorHandler = ({
  graphQLErrors,
  networkError,
  operation,
  response,
}) => {
  if (graphQLErrors) {
    const errorMessages = graphQLErrors.map((error) => error.message);

    const errorContext = {
      messages: errorMessages,
      operation: JSON.stringify(operation),
      response: JSON.stringify(response),
    };

    // eslint-disable-next-line no-console
    isDevelopment() && console.error('[GraphQL error]: ', errorContext);

    if (isEqual(errorMessages, ['unauthorized'])) {
      window.dispatchEvent(new CustomEvent('graphql_unauthorized_error'));
    } else if (isEqual(errorMessages, ['not_found'])) {
      // Do not report not found errors
    } else {
      Sentry.captureException(
        new Error('GraphQL error on ' + operation?.operationName),
        { extra: errorContext },
      );
    }
  }
  if (networkError && isDevelopment())
    // eslint-disable-next-line no-console
    isDevelopment() && console.error(`[Network error]: ${networkError}`);
};

const errorHandlingLink = onError(errorHandler);

const retryLink = new RetryLink({
  delay: {
    initial: 300,
    jitter: true,
  },
  attempts: {
    max: 5,
    retryIf: (error, _operation) => {
      return [408, 500, 502, 503, 504].includes(error.statusCode);
    },
  },
});

const sentryLink = new SentryLink({
  attachBreadcrumbs: {
    includeQuery: true,
    includeVariables: true,
    includeFetchResult: true,
    includeError: true,
  },
  setTransaction: false,
});

export const apolloClient = new ApolloClient({
  link: from([errorHandlingLink, retryLink, sentryLink, httpLink]),
  cache: new InMemoryCache({
    possibleTypes: introspectionQueryResultData.possibleTypes,
  }),
  connectToDevTools: true,
  defaultOptions: {
    query: {
      fetchPolicy: 'cache-first',
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
});

export default function withGraphQL<P extends object>(
  Component: React.ComponentType<P>,
) {
  function EnhancedComponent(props: React.Attributes) {
    return (
      <ApolloProvider client={apolloClient}>
        <Component {...(props as P)} />
      </ApolloProvider>
    );
  }

  return EnhancedComponent;
}
